import {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { t } from 'i18n-js';

import { NOOP } from '@lootboy/lib/utils';

import ButtonClean from '~/components/ButtonClean';
import NewTabOpener from '~/components/NewTabOpener';

import { ReactComponent as CopyIcon } from '~/assets/images/iconsLibrary/copy.svg';

import styles from './OfferTicket.module.scss';

type TOfferTicketProps = {
  code: string;
  link: string;
  linkLabel: string;
  isDisplayed: boolean;
  isLinkInline?: boolean;
};

export type TOfferTicketRef = {
  scroll: () => void;
};

function OfferTicketRenderer(
  {
    linkLabel,
    link,
    code,
    isDisplayed,
    isLinkInline = false,
  }: TOfferTicketProps,
  ref: ForwardedRef<TOfferTicketRef>,
) {
  const ticketRef = useRef<HTMLDivElement>(null);
  const [isCopied, setCopied] = useState(false);

  const onCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch (error) {
      NOOP();
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      scroll() {
        if (ticketRef.current) {
          ticketRef.current.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
          });
        }
      },
    }),
    [],
  );

  useEffect(() => {
    if (isDisplayed) {
      setCopied(false);
    }
  }, [isDisplayed, setCopied]);

  return (
    <div ref={ticketRef}>
      <svg className={styles.dashedLine}>
        <line
          className={styles.line}
          strokeDasharray={2}
          y1={0}
          y2={0}
          x1={0}
          x2={1000}
        />
      </svg>
      <div className={styles.container}>
        <div
          className={classNames(
            styles.ticketContainer,
            isLinkInline && styles.inline,
          )}
        >
          <p className={styles.codeInfo}>{t('deals_code_label')}</p>
          <div className={styles.codeContainer}>
            <h3 className={styles.code}>
              {isCopied ? t('common_copied_to_clipboard_title') : code}
            </h3>
            <ButtonClean
              className={styles.copyButton}
              onClick={onCopyClick}
            >
              {t('common_copy')}
              <CopyIcon className={styles.copyIcon} />
            </ButtonClean>
          </div>
        </div>
        {isLinkInline && (
          <div className={styles.linkContainer}>
            <NewTabOpener
              href={link}
              className={styles.link}
            >
              {linkLabel}
            </NewTabOpener>
          </div>
        )}
      </div>
      {!isLinkInline && (
        <NewTabOpener
          href={link}
          className={classNames(styles.link, styles.bottom)}
        >
          {linkLabel}
        </NewTabOpener>
      )}
    </div>
  );
}

export const OfferTicket = forwardRef(OfferTicketRenderer);
